/**
 * F09 (customer audit, 10 October 2026)
 * ---------------------------------------------------------------------------
 * "Live cart understates the amount needed for free delivery."
 *
 * The free-shipping bar repainted from whichever cart payload arrived last. A
 * quantity edit and a removal fire overlapping requests, so a late reply could
 * repaint the bar with an older cart. The message was then wrong until the page
 * was reloaded. The bar also measured cart.total_price while the Liquid first
 * paint seeds the merchandise subtotal, so the two could disagree on a cart
 * carrying a discount.
 *
 * This module leaves <free-shipping-bar> in theme.js alone and wraps its render:
 *   1. the optimistic value still paints immediately, so the bar stays responsive;
 *   2. once the edits settle, the real cart is fetched once and the bar is
 *      repainted from cart.items_subtotal_price - the same basis Liquid uses.
 *
 * It is deliberately a separate file so it can be deleted in one step. When
 * theme.js is next rebuilt, fold this into the component and remove both this
 * file and its <script> tag in layout/theme.liquid.
 */
(() => {
  'use strict';

  const SETTLE_MS = 400;

  customElements.whenDefined('free-shipping-bar').then(() => {
    const FreeShippingBar = customElements.get('free-shipping-bar');
    if (!FreeShippingBar || FreeShippingBar.prototype.__auditF09) return;

    const originalRender = FreeShippingBar.prototype.render;

    /* The threshold is a promise about merchandise, so measure the item subtotal. */
    const subtotalOf = (cart) => {
      if (!cart) return null;
      if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;
      if (typeof cart.total_price === 'number') return cart.total_price;
      return null;
    };

    const timers = new WeakMap();

    const confirmAgainstCart = (bar) => {
      window.clearTimeout(timers.get(bar));
      timers.set(
        bar,
        window.setTimeout(async () => {
          if (!bar.isConnected) return;
          try {
            const cart = await window.theme.cartApi.get();
            const subtotal = subtotalOf(cart);
            if (subtotal !== null) originalRender.call(bar, subtotal);
          } catch (error) {
            /* keep the optimistic value if the check cannot be made */
          }
        }, SETTLE_MS)
      );
    };

    FreeShippingBar.prototype.render = function render(subtotal) {
      originalRender.call(this, subtotal);
      /* The first paint comes from Liquid and is already authoritative, so only
         verify once the cart has actually changed. */
      if (this.__auditF09Primed) confirmAgainstCart(this);
      this.__auditF09Primed = true;
    };

    FreeShippingBar.prototype.__auditF09 = true;
  });
})();
